import type { ImageObject } from 'schema-dts';

/** Attribution shared by every image on a page. */
export interface ImageAttribution {
    /** Name shown as the attribution, normally the business that supplied the photo. */
    creditText?: string;
    /** Business credited as the creator of the photo. */
    creatorName?: string;
}

/**
 * Builds an `ImageObject` for a single image.
 *
 * Plain URL strings satisfy the `image` property but carry nothing beyond the file location.
 * `ImageObject` lets each photo declare its caption and attribution, which is what Google Images
 * shows next to a result and what makes a large photo library discoverable as more than page
 * decoration.
 *
 * Licence fields (`license`, `acquireLicensePage`) are deliberately absent. They advertise that the
 * image can be licensed, which is not true of professional-supplied photography on ArchiPro, and a
 * false claim in structured data is worse than an absent one.
 *
 * Returns undefined for a blank URL so callers can filter without repeating the check.
 */
export function buildImageObject(
    url: string | null | undefined,
    attribution: ImageAttribution & {
        caption?: string;
        /** True when the image is the page's lead photo rather than one of many. */
        representativeOfPage?: boolean;
    } = {},
): ImageObject | undefined {
    const contentUrl = url?.trim();

    if (!contentUrl) {
        return undefined;
    }

    const { caption, creditText, creatorName, representativeOfPage } =
        attribution;

    return {
        '@type': 'ImageObject',
        contentUrl,
        url: contentUrl,
        ...(caption && { caption }),
        ...(creditText && { creditText }),
        ...(creatorName && {
            creator: { '@type': 'Organization', name: creatorName },
            copyrightNotice: creatorName,
        }),
        ...(representativeOfPage && { representativeOfPage: true }),
    };
}

/**
 * Builds `ImageObject`s for a page's gallery, marking the first as the representative image.
 *
 * Blank entries are dropped rather than emitted as empty objects.
 */
export function buildImageObjects(
    urls: Array<string | null | undefined>,
    attribution: ImageAttribution = {},
): ImageObject[] {
    return urls
        .map((url, index) =>
            buildImageObject(url, {
                ...attribution,
                representativeOfPage: index === 0,
            }),
        )
        .filter((image): image is ImageObject => image !== undefined);
}
